import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import store from "store";
import {
  acceptInvitation,
  addUser,
  changeLanguage,
  changePassword,
  deleteUser,
  generateOtp,
  getInhouseProfile,
  getInits,
  getPhoneProfile,
  getUserRegisterByToken,
  getUsers,
  inviteUserWithRegister,
  logout,
  requestRecovery,
  resendInvitation,
  reset,
  revokeLogin,
  singleLogin,
  updateProfile,
  updateUser,
  verifyGoogleToken,
  verifyToken,
} from "apis/user";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  logout as reduxLogout,
  resetData,
  setApp,
  setConfig,
  setLoadingInit,
  setUser,
} from "appredux/configSlice";
import { clearAuthTokens, setAuthTokens, setSavedEmail } from "utils/cookies";
import { generateBaseUri } from "utils/helper";
import Axios from "utils/axios";
import customAxios from "utils/axios";
import { selectedCompanySelector } from "appredux/selectors";
import { resetChat } from "appredux/chatSlice";
import { settingMenus } from "utils/static";
import pusher from "utils/pusher";
import { useAntdMessage } from "hooks/useAntdMessage";
import { isEmpty, isString } from "lodash-es";

// redirectTo: null → tetap di halaman saat ini (mis. halaman undangan yang
// meminta logout dulu sebelum undangan diproses)
export const useLogout = ({ redirectTo = "/user/login" } = {}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedCompany = useSelector(selectedCompanySelector);
  const { message } = useAntdMessage();

  return useMutation({
    // payload: { token, refreshToken } — diambil sebelum onMutate menghapus sesi lokal
    mutationFn: (payload) => logout(payload),
    onMutate: () => {
      // Dihapus dulu previous path dan selected endpoinnya
      store.remove("app.endpoint");
      store.remove("app.bootstrapEndpoint");
      store.remove("app.previousPathname");
      // Axios.defaults.baseURL = generateBaseUri();

      // Melakukan unsubscribe dari semua channel
      pusher.unsubscribe(
        `private-wa-sync-changed-${selectedCompany?.website_id}`,
      );
      pusher.disconnect();

      // Dihapus seluruh token (access + refresh)
      clearAuthTokens();
      // Reset reduxnya
      dispatch(reduxLogout());
      dispatch(resetChat());
      // Memutuskan koneksi Pusher

      if (redirectTo) {
        navigate(redirectTo, { replace: true });
      }
    },
    onSuccess: (response) => {
      if (!response.data.success) {
        message.error(response.data.message);
      }
    },
    onSettled: () => {
      Axios.defaults.baseURL = generateBaseUri();
    },
  });
};

const defaultCustomOptions = {
  withSetEndpoint: false,
  withSetEmail: true,
};

export const useSingleLogin = (customOptions = {}) => {
  customOptions = { ...defaultCustomOptions, ...customOptions };
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: ({ email, password, is_otp }) =>
      singleLogin(email, password, 1, is_otp),
    onSuccess: (response) => {
      if (response?.data.success && response?.data?.data?.otp_required !== 1) {
        successLogin(response.data?.data, dispatch, customOptions, queryClient);
      }
    },
  });
};

// Callback untuk login sukses.
// BE TIDAK lagi mengirim `init` di response /authentication. Setelah login kita
// hanya menyiapkan token + endpoint perusahaan; data inits diambil mandiri oleh
// useLoadInits (sama persis seperti saat refresh browser dalam keadaan login).
export const successLogin = (
  { data, companies = [], user },
  dispatch,
  customOptions = {},
  queryClient,
) => {
  queryClient.clear();
  customOptions = { ...defaultCustomOptions, ...customOptions };

  setAuthTokens(data);
  if (customOptions.withSetEmail) {
    setSavedEmail(user?.email);
  }

  const config = {
    apiToken: data?.access_token,
    expiredToken: data?.expires_at,
    locale: user?.lang || "id-ID",
  };

  // Single company: langsung set endpoint perusahaan tunggal, lalu biarkan
  // useLoadInits yang menembak /WA/inits. user/loadedInit SENGAJA tidak diisi
  // supaya gate useLoadInits (`!user.id && !loadedInit`) terbuka di Main layout,
  // dan MainPage ("/") yang menentukan tujuan akhir setelah inits siap.
  if (companies.length === 1) {
    const company = companies[0];
    config.endpoint = company.endpoint;
    config.selectedCompany = company;
    store.set("app.endpoint", company.endpoint); // dipakai generateBaseUri()
    Axios.defaults.baseURL = generateBaseUri();
    dispatch(setConfig(config));
    dispatch(setApp({ companies }));
    return;
  }

  // Multi company: set user + loadedInit=true supaya useLoadInits TIDAK fetch
  // prematur di halaman /user/select-company (endpoint belum dipilih). inits
  // baru di-fetch setelah perusahaan dipilih (selectCompanyHandler -> resetData).
  // app.bootstrapEndpoint: endpoint perusahaan pertama, HANYA untuk inits saat
  // halaman pilih perusahaan di-refresh (redux hilang, inits di base URL awal 404).
  // Sengaja bukan app.endpoint — itu dibaca sebagai "perusahaan sudah dipilih"
  // dan akan langsung melewati halaman pilih perusahaan.
  if (companies[0]?.endpoint) {
    store.set("app.bootstrapEndpoint", companies[0].endpoint);
  }
  dispatch(setConfig(config));
  dispatch(
    setApp({
      user: { ...user, menus: { settings: settingMenus } },
      companies,
      loadedInit: true,
    }),
  );
};

export function useInits(options = {}, params = {}, config = {}) {
  const { notification } = useAntdMessage();
  const dispatch = useDispatch();
  return useQuery({
    queryKey: ["inits", params],
    queryFn: async () => {
      dispatch(setLoadingInit(true));
      let response;
      try {
        const {
          data: { data },
        } = await getInits(params, config);
        response = data;
      } catch ({ response: error }) {
        if (
          error &&
          error.status &&
          (error.status === 401 || error.status === 404)
        ) {
          store.remove("app.endpoint");
          store.remove("app.bootstrapEndpoint");
          store.remove("app.previousPathname");
          customAxios.defaults.baseURL = generateBaseUri();
          clearAuthTokens();
          dispatch(resetData());
          notification.warning({
            description: "Login expired",
            key: "Login expired",
          });
        }
        throw new Error("Gagal mengambil data dari server!");
      }
      dispatch(setLoadingInit(false));
      return response;
    },
    ...options,
  });
}

export const useChangeLanguage = () => {
  const { t } = useTranslation();
  const { message } = useAntdMessage();

  // const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: ({ locale }) => changeLanguage(locale),
    onSuccess: (response, { locale }) => {
      if (response?.data.success) {
        // queryClient.setQueryData('inits', () => response.data?.data?.init);
        store.set("app.settings.locale", locale);
        dispatch(setConfig({ locale }));
        dispatch(
          setApp({
            ...response.data?.data?.init,
            website_id: parseInt(response.data?.data?.init?.website_id, 10),
          }),
        );
        // dispatch(setMenu({ ...response.data?.data?.init?.user?.menus }));
        // dispatch(setReference({ ...response.data?.data?.init?.reference }));
        message.success(response.data?.message);
      }
    },
    onError: (_err) => {
      message.error(
        !isEmpty(_err) && isString(_err) ? _err : t("error.an_error_happened"),
      );
    },
  });
};

export const useUpdateProfile = () => {
  const { message } = useAntdMessage();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (payload) => updateProfile(payload),
    onSuccess: (response) => {
      if (response.data.success) {
        const data = response.data.data || {};
        if (!data.otp_required) {
          queryClient.setQueryData(["inits"], (old) => {
            if (old?.user) {
              const user = { ...old.user };
              old.user = { ...user, ...response.data.data };
              return { ...old };
            }
            return old;
          });
          dispatch(setUser({ ...response.data.data }));
          message.success(response.data?.message);
        }
      } else {
        message.error(response.data?.message);
      }
    },
    onError: () => {
      message.error(t("profile.error_cannot_update_user_profile"));
    },
  });
};

export const useChangePassword = () => {
  const { message } = useAntdMessage();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (payload) => changePassword(payload),
    onSuccess: (response) => {
      if (response.data.success) {
        message.success(response.data?.message);
      } else {
        message.error(response.data?.message);
      }
    },
    onError: () => {
      message.error(t("profile.error_cannot_update_user_password"));
    },
  });
};

export const useRevokeLogin = () => {
  const { message } = useAntdMessage();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: () => revokeLogin(),
    onSuccess: (response) => {
      if (response.data.success) {
        message.success(response.data?.message);
      } else {
        message.error(response.data?.message);
      }
    },
    onError: () => message.error(t("'profile.cannot_logout_from_all_device'")),
  });
};
export const useRequestRecovery = () => {
  const { message } = useAntdMessage();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (payload) => requestRecovery(payload),
    onError: (_err) => {
      message.error(
        !isEmpty(_err) && isString(_err) ? _err : t("error.an_error_happened"),
      );
    },
  });
};

export function useVerifyToken(payload = {}, options = {}) {
  return useQuery({
    queryKey: ["verifyToken/resetPassword"],
    queryFn: async () => {
      let response;
      try {
        const {
          data: { data },
        } = await verifyToken(payload.token);
        response = data;
      } catch (error) {
        throw new Error("Gagal mengambil data dari server!");
      }
      return response;
    },
    ...options,
  });
}

export const useResetPassword = () => {
  const { message } = useAntdMessage();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => reset(payload),
    onSuccess: (response) => {
      if (response?.data.success) {
        successLogin(response.data?.data, dispatch, {}, queryClient);
      }
    },
    onError: (_err) => {
      message.error(
        !isEmpty(_err) && isString(_err) ? _err : t("error.an_error_happened"),
      );
    },
  });
};

export function useUsers({ options = {} } = {}) {
  return useQuery({
    queryKey: ["users"],
    queryFn: getUsers,
    select: (data) => data.data.data,
    ...options,
  });
}

export const useAddUserMutation = () => {
  return useMutation({
    mutationFn: addUser,
  });
};

export const useUpdateUserMutation = () => {
  return useMutation({
    mutationFn: (payload) => updateUser(payload.data, payload.id),
  });
};

export const useDeleteUserMutation = () => {
  return useMutation({
    mutationFn: deleteUser,
  });
};

export const useResendInvitationMutation = () => {
  return useMutation({
    mutationFn: resendInvitation,
  });
};

export const useVerifyGoogleToken = (customOptions = {}) => {
  customOptions = { ...defaultCustomOptions, ...customOptions };
  const { message } = useAntdMessage();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: ({ access_token }) => verifyGoogleToken(access_token),
    onSuccess: (response) => {
      if (response?.data.success) {
        successLogin(response.data?.data, dispatch, customOptions, queryClient);
      } else {
        message.error(response.data?.message || t("error.an_error_happened"));
      }
    },
  });
};
export const useUserRegisterByToken = ({ payload, options = {} } = {}) => {
  return useQuery({
    queryKey: ["user-by-token", payload],
    queryFn: async () => {
      let response;
      try {
        const {
          data: { data },
        } = await getUserRegisterByToken(payload);
        response = data;
      } catch (error) {
        throw new Error(error?.data?.message);
      }
      return response;
    },
    ...options,
  });
};

export const useInviteUserWithRegisterMutation = () => {
  return useMutation({
    mutationFn: inviteUserWithRegister,
  });
};

export const useAcceptInvitationMutation = () => {
  return useMutation({ mutationFn: acceptInvitation });
};

export const useGenerateOtp = () => {
  return useMutation({ mutationFn: generateOtp });
};

export function usePhoneProfile(params = {}, options = {}) {
  return useQuery({
    queryKey: ["users/phone-profile", params],
    queryFn: () => getPhoneProfile(params),
    select: (data) => data.data.data,
    ...options,
  });
}

export function useInhouseProfile(params = {}, options = {}) {
  return useQuery({
    queryKey: ["users/inhouse-profile", params],
    queryFn: () => getInhouseProfile(params),
    select: (data) => data.data.data,
    ...options,
  });
}
